<template>
  <div class="user-profile card px-3 py-3">
    <b-overlay :show="isLoading" rounded="sm">
      <div class="user-profile--header d-flex justify-content-between flex-wrap">
        <div class="user-profile--summary d-flex flex-wrap">
          <avatar class="avatar" :username="username" :size="100"></avatar>
          <div class="px-2 py-2 d-flex flex-column justify-content-center">
            <h4>{{ originUser.fname }} {{ originUser.lname }}</h4>
            <h5 v-if="currentAgency">{{ currentAgency.agency_name }}</h5>
            <div class="user-profile--roles d-flex align-items-center flex-wrap">
              <span
                v-for="role in userRoles()"
                :key="`${originUser.user_id}_${role}`"
                :class="`${getRoleType(role)} badge font-weight-medium text-capitalize`"
              >
                {{ getRoleLabel(role) }}
              </span>
            </div>
          </div>
        </div>
        <div class="user-profile--actions d-flex align-items-end flex-wrap">
          <b-button pill variant="info" @click="toggleEdit()">Edit Profile</b-button>
          <b-button pill variant="info" @click="changePassword()">Change Password</b-button>
        </div>
      </div>
      <hr />
      <div class="user-profile--content d-flex flex-column">
        <div v-if="errorMsg" class="text-danger mb-3">
          {{ errorMsg }}
        </div>
        <b-row v-if="originUser && originUser.user_id">
          <b-col cols="12" lg="6">
            <b-form-group
              label="First Name"
              :state="!hasErrors('fname')"
              :invalid-feedback="errors['fname']"
            >
              <b-form-input
                v-model="currentUser.fname"
                placeholder="Enter your first name"
                :disabled="!editMode"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="6">
            <b-form-group
              label="Last Name"
              :state="!hasErrors('lname')"
              :invalid-feedback="errors['lname']"
            >
              <b-form-input
                v-model="currentUser.lname"
                placeholder="Enter your last name"
                :disabled="!editMode"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="6">
            <b-form-group
              label="Email Address"
              :state="!hasErrors('email')"
              :invalid-feedback="errors['email']"
            >
              <b-form-input
                type="email"
                v-model="currentUser.email"
                placeholder="Enter your email"
                :disabled="true"
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="6">
            <b-form-group
              label="Phone"
              :state="!hasErrors('phone')"
              :invalid-feedback="errors['phone']"
            >
              <b-form-input
                v-model="formattedNumber"
                :disabled="!editMode"
                placeholder="Enter your phone number..."
              ></b-form-input>
            </b-form-group>
          </b-col>
          <b-col v-if="!editMode" cols="12" lg="6">
            <b-form-group label="Created">
              <h6>{{ formatTimestamp(currentUser.created_at, "LLLL") }}</h6>
            </b-form-group>
          </b-col>
          <b-col cols="12" lg="6">
            <b-form-group
              label="Signature"
              description="Your signature can be appended to a conversation's opening message"
            >
              <b-form-textarea
                v-model="currentUser.settings.signature"
                placeholder="Enter your signature..."
                :disabled="!editMode"
              ></b-form-textarea>
            </b-form-group>
          </b-col>
        </b-row>
        <div v-if="editMode" class="d-flex justify-content-end align-items-center flex-wrap" style="gap:8px;">
          <b-button variant="success" @click="saveProfile()" pill size="sm">
            <b-spinner v-if="savePending === true" small></b-spinner>
            <span v-else>Save</span>
          </b-button>
          <b-button @click="toggleEdit(false)" pill size="sm">
            <span>Cancel</span>
          </b-button>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import { EventBus } from "../../events/eventBus";
import formatter from "../../mixins/formatter";
import ChangePassword from "./ChangePassword";
import Avatar from "vue-avatar";

export default {
  components: {
    Avatar,
  },
  data() {
    return {
      currentUser: {},
      originUser: {},
      userSettings: {},
      isLoading: false,
      editMode: false,
      savePending: false,
      errors: {},
      errorMsg: null,
      formattedNumber: null,
    };
  },
  mixins: [formatter],
  computed: {
    ...mapGetters("agency", ["currentAgency", "currentAgencyId"]),
    ...mapGetters("user", ["currentToken"]),
    error: function () {
      if (this.errors && Object.keys(this.errors).length > 0)
        return "Invalid input. Please correct the errors below and try again.";

      return false;
    },
    username() {
      return `${this.originUser.fname} ${this.originUser.lname}`;
    },
  },
  methods: {
    async saveProfile() {
      this.errorMsg = null;
      this.errors = {};

      this.currentUser.phone = this.formattedNumber;
      if (!this.currentUser.fname || this.currentUser.fname == "")
        this.errors["fname"] = "Please enter your first name";
      if (!this.currentUser.lname || this.currentUser.lname == "")
        this.errors["lname"] = "Please enter your last name";
      if (
        this.currentUser.phone &&
        this.currentUser.phone.replace(/[^0-9]/g, "").length != 10
      ) {
        this.errors["phone"] = "Please enter a valid phone Number";
      }

      if (Object.keys(this.errors).length > 0) return false;

      try {
        this.savePending = true;
        const payload = {
          fname: this.currentUser.fname,
          lname: this.currentUser.lname,
          email: this.currentUser.email,
          settings: [{
            name: "signature",
            value: this.currentUser.settings.signature,
            component: "user",
          }],
        };
        if (this.currentUser.phone) {
          payload["phone"] = this.currentUser.phone.replace("-", "");
        }

        const resp = await this.$ConveyApi.usersApi.updateCurrentUser({ agencyId: this.currentAgencyId, payload });
        await this.fetchCurrentUser();
        this.$toastr("success", "User profile has been updated.");
        this.toggleEdit(false);
      } catch (err) {
        this.errorMsg = err.response.data.data.error;
      } finally {
        this.savePending = false;
      }
    },
    hasErrors(field) {
      if (this.errors[field]) return "text-danger";

      return "";
    },
    toggleEdit(editMode = true) {
      this.editMode = editMode;
    },
    async fetchCurrentUser() {
      try {
        this.isLoading = true;
        const resp = await this.$ConveyApi.usersApi.getCurrentUser({ agencyId: this.currentAgencyId });
        this.currentUser = {...resp.data.data};
        this.originUser = {...resp.data.data};

        if (this.currentUser.phone) {
          this.currentUser.phone = this.currentUser.phone.replace(/[^0-9]/g, "");
          if (this.currentUser.phone.length == 10) {
            this.currentUser.phone = `+1${this.currentUser.phone}`;
          }
          this.formattedNumber = this.prettyPhoneNumber(this.currentUser.phone);
        }
        this.userSettings = this.currentUser.settings;
      } catch(err) {
        console.log(err);
        this.currentUser = {};
      } finally {
        this.isLoading = false;
      }
    },
    userRoles() {
      let roles = [];
      if (this.originUser && Array.isArray(this.originUser.roles))
        roles = [...this.originUser.roles];

      return roles;
    },
    getRoleType(roleId) {
      const role = this.availableRoles.find(item => item.value === roleId);
      if (role) {
        const roleText = role.text.toLowerCase();
        if (roleText.includes('admin')) {
          return 'admin';
        } else if (roleText.includes('operator')) {
          return 'operator';
        } else {
          return 'other';
        }
      } else {
        return 'unknown'
      }
    },
    getRoleLabel(roleId) {
      const role = this.availableRoles.find(item => item.value === roleId);
      if (role) {
        return role.text;
      } else {
        return 'Unknown'
      }
    },
    changePassword() {
      const $ = window.$;
      $("#id-modal-container").modal();
      EventBus.$emit("open", {
        component: ChangePassword,
        title: "Change Your Password",
      });
    },
    focusOut: function (event) {
      if (["Arrow", "Backspace", "Shift"].includes(event.key)) {
        this.preventNextIteration = true;
        return;
      }
      if (this.preventNextIteration) {
        this.preventNextIteration = false;
        return;
      }

      let phone = this.formattedNumber;
      try {
        if (this.formattedNumber && this.formattedNumber.length > 0)
          phone = this.formattedNumber
            .replace(/[^0-9]/g, "")
            .match(/(\d{1,10})/g)[0];
      } catch (err) {
        console.log(err);
      }

      if (phone && phone.length == 3) this.formattedNumber = phone + "-";
      if (phone && phone.length == 6)
        this.formattedNumber = phone.replace(/^(\d{1,3})(\d{1,3})$/g, "$1-$2-");
    },
  },
  created() {
    try {
      this.availableRoles = this.currentAgency.roles.map(r => ({ ...r, text: r.role, value: r.role_id }));
    } catch (err) { }
    this.fetchCurrentUser();
  },
};
</script>

<style lang="scss">
.user-profile {
  border-radius: 1rem;

  &--header {
    gap: 8px;
  }

  &--content {
    gap: 8px;
  }

  &--summary {
    gap: 8px;
    h4, h5 {
      text-wrap: nowrap;
      white-space: nowrap;
    }
  }

  &--roles {
    gap: 4px;
    span.badge.admin {
      background-color: #f3ced6 !important;
      color: #ef5350 !important;
    }

    span.badge.operator {
      background-color: #b7f1e0 !important;
      color: #0acc95 !important;
    }

    span.badge.other {
      background-color: #cbe2ff !important;
      color: #186dde !important;
    }

    span.badge.unknown {
      background-color: #cbe2ff !important;
      color: #186dde !important;
    }
  }

  &--actions {
    gap: 8px;
  }
}
</style>
